import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { I18n } from 'aws-amplify';
import logo from './assets/Adfectus_logo.png';

import Groups from "./Views/Groups";
import Users from "./Views/Users";
import Settings from "./Views/Settings";
import Groupinfo from "./Views/Groupinfo";
import Userinfo from "./Views/Userinfo";
import NewUser from './Views/NewUser'
import MainView from './Views/MainView'
import useInactivityLogout from './hooks/useInactivityLogout'

import "@aws-amplify/ui-react/styles.css";
import './App.css';
import {
  View,
  withAuthenticator,
} from "@aws-amplify/ui-react";

// Sign the user out automatically after 30 minutes of inactivity.
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
// Warn the user this many milliseconds before actually signing them out.
const INACTIVITY_WARNING_MS = 30 * 1000;

I18n.putVocabularies({
  no: {
    'Sign in': 'Logg inn',
    'Sign In': 'Logg inn',
    'Sign up': 'Registrer deg',
    'Sign Up': 'Registrer deg',
    'Create Account': 'Opprett konto',
    'Username': 'Brukernavn',
    'Password': 'Passord',
    'Enter your Username': 'Skriv inn brukernavn',
    'Enter your Password': 'Skriv inn passord',
    'Forgot your password?': 'Glemt passordet?',
    'Reset password': 'Tilbakestill passord',
    'Reset your password': 'Tilbakestill passordet ditt',
    'Enter your username': 'Skriv inn brukernavn',
    'Send code': 'Send kode',
    'Submit': 'Send inn',
    'Back to Sign In': 'Tilbake til innlogging',
    'Change Password': 'Endre passord',
    'New Password': 'Nytt passord',
    'Confirm Password': 'Bekreft passord',
    'Confirm Sign Up': 'Bekreft registrering',
    'Confirmation Code': 'Bekreftelseskode',
    'Confirm TOTP Code': 'Bekreft engangskode',
    'Setup TOTP': 'Sett opp tofaktorautentisering',
    'Scan the QR code below with your authenticator app': 'Skann QR-koden nedenfor med autentiseringsappen din',
    'Alternatively, enter the following code manually': 'Du kan også skrive inn denne koden manuelt',
    'Code': 'Kode',
    'Confirm': 'Bekreft',
    'Resend Code': 'Send kode på nytt',
    'Show password': 'Vis passord',
    'Hide password': 'Skjul passord',
    'Password must have at least 8 characters': 'Passordet må ha minst 8 tegn',
    'This field is required': 'Dette feltet er obligatorisk',
  },
});
I18n.setLanguage('no');

const centerTotpSetup = () => {
  const totpHosts = document.querySelectorAll('amplify-totp-setup, amplify-totp-setup-interface');

  totpHosts.forEach((totpHost) => {
    const shadowRoot = totpHost.shadowRoot;
    if (!shadowRoot || shadowRoot.querySelector('[data-adminapp-totp-style]')) return;

    const style = document.createElement('style');
    style.setAttribute('data-adminapp-totp-style', 'true');
    style.textContent = `
      .totp-setup {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        text-align: center !important;
      }

      .totp-setup > img {
        display: block !important;
        margin: 0 auto 14px !important;
      }

      amplify-copy,
      [data-test*="secret"],
      [class*="secret"] {
        display: none !important;
      }
    `;
    shadowRoot.appendChild(style);
  });
};

if (typeof document !== 'undefined') {
  const totpObserver = new MutationObserver(centerTotpSetup);
  totpObserver.observe(document.documentElement, { childList: true, subtree: true });
  centerTotpSetup();
}


const theme = createTheme({
  palette: {
    primary: {
      main: '#00B6CD',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#b1ebf4'
    },
    custom: {
      main: '#00C1B3',
      contrastText: '#FFFFFF',
    },
    white: {
      main: '#FFFFFF'
    }
  }
});

function App({ signOut }) {
  const { showWarning, secondsLeft, stayActive } = useInactivityLogout(
    signOut,
    INACTIVITY_TIMEOUT_MS,
    INACTIVITY_WARNING_MS
  );

  return (
    <ThemeProvider theme={theme}>
      <View className="App">
        <Router>
          <Routes>
            <Route exact path="/" element={<MainView signOut={signOut} />} />
            <Route exact path="/groups" element={<Groups />} />
            <Route exact path="/users" element={<Users />} />
            <Route exact path="/settings" element={<Settings />} />
            <Route exact path="groupinfo/:group" element={<Groupinfo />} />
            <Route exact path="userinfo/:user" element={<Userinfo />} />
            <Route exact path="/newuser" element={<NewUser />} />
          </Routes>
        </Router>
        <Dialog open={showWarning} onClose={stayActive}>
          <DialogTitle>Du blir snart logget ut</DialogTitle>
          <DialogContent>
            <DialogContentText>
              På grunn av inaktivitet blir du logget ut om {secondsLeft} sekund{secondsLeft === 1 ? '' : 'er'}.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={stayActive} variant="contained">Forbli innlogget</Button>
          </DialogActions>
        </Dialog>
      </View>
    </ThemeProvider>
  );
}

function AuthenticatorHeader() {
  return (
    <View className="adfectus-auth-header">
      <img src={logo} alt="Adfectus" />
    </View>
  );
}

export default withAuthenticator(App, {
  components: {
    Header: AuthenticatorHeader,
  },
});

